/**
 * Practice history: an append-only, on-device log of completed practice
 * takes, in its own IndexedDB database (never the seed bundle's — see
 * `seed.ts`'s `DB_NAME`/`STORE_NAME`, whose "tiny promisified IndexedDB
 * wrapper" pattern this mirrors). Grows over a student's lifetime, so it
 * lives in IndexedDB rather than localStorage (contrast `profile.ts`, which
 * is small + read-often and stays in localStorage).
 *
 * Guarded throughout: offline/private-mode/`indexedDB` missing never throws
 * — reads resolve to empty results (`[]` / `0` / `null`), writes are a
 * best-effort no-op. Phase A's data foundation must never block practice.
 */

const DB_NAME = 'swarasense-history';
const STORE_NAME = 'sessions';

export interface PracticeSession {
  id: string;
  dateISO: string;
  raga: string;
  level: string;
  mode: 'free' | 'drill' | 'ear';
  inRagaPct?: number;
  wellCentredPct?: number;
  gamakaAdherencePct?: number;
  weakestSwara?: string;
  durationSec?: number;
}

/** Tiny promisified IndexedDB wrapper — same shape as `seed.ts`'s `openDb`. */
function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE_NAME)) {
        req.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error as unknown);
  });
}

/** A fresh id for a new session record. Not pure (uses `crypto.randomUUID`
 * where available, a timestamp+random fallback otherwise) — deliberately
 * kept out of the pure `personalization.ts` helpers, but fine here: this is
 * web app storage code, not shared/pure logic. */
function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Append a completed practice session to the history log. Best-effort: if
 * IndexedDB is unavailable or the write fails (offline/private-mode storage
 * limits), this resolves without throwing — a failed history write must
 * never interrupt the practice flow that produced it.
 */
export async function recordSession(s: Omit<PracticeSession, 'id'>): Promise<void> {
  const session: PracticeSession = { ...s, id: generateId() };
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).add(session);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error as unknown);
    });
  } catch {
    // IndexedDB unavailable/write failed — nothing persists this session, fine.
  }
}

/** Every recorded session, unordered — `[]` if none yet or IndexedDB is
 * unavailable (never throws). Callers wanting an order should use
 * `recentSessions`, which sorts by `dateISO` descending. */
export async function allSessions(): Promise<PracticeSession[]> {
  try {
    const db = await openDb();
    return await new Promise<PracticeSession[]>((resolve, reject) => {
      const req = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAll();
      req.onsuccess = () => resolve(req.result as PracticeSession[]);
      req.onerror = () => reject(req.error as unknown);
    });
  } catch {
    return [];
  }
}

/** The `n` most recently practised sessions, most-recent first (sorted by
 * `dateISO` descending — an ISO-8601 string sorts lexicographically the same
 * as chronologically). `[]` if there's no history yet. */
export async function recentSessions(n: number): Promise<PracticeSession[]> {
  const all = await allSessions();
  return [...all].sort((a, b) => b.dateISO.localeCompare(a.dateISO)).slice(0, n);
}

/** Total number of recorded sessions ever, `0` if none/unavailable. */
export async function sessionCount(): Promise<number> {
  const all = await allSessions();
  return all.length;
}

/** The most recent session's `dateISO`, or `null` if nothing's been
 * recorded yet (or IndexedDB is unavailable) — the "You" view's "last
 * practised {date}" line. */
export async function lastPractisedISO(): Promise<string | null> {
  const all = await allSessions();
  if (all.length === 0) return null;
  return all.reduce((latest, s) => (s.dateISO > latest ? s.dateISO : latest), all[0]!.dateISO);
}
